import { useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { cx } from '../../lib/format';

const MEMBER_NAV = [
  { label: 'Overview', to: '/member', icon: '🏠', end: true },
  { label: 'My Stories', to: '/member/stories', icon: '📝' },
  { label: 'Submit Story', to: '/member/stories/new', icon: '➕' },
  { label: 'My News', to: '/member/news', icon: '📰' },
  { label: 'Submit News', to: '/member/news/new', icon: '🗞️' },
  { label: 'My Activities', to: '/member/activities', icon: '📸' },
  { label: 'Submit Activity', to: '/member/activities/new', icon: '🤝' },
  { label: 'Profile', to: '/member/profile', icon: '👤' },
  { label: 'Change Password', to: '/member/password', icon: '🔒' },
];

const ADMIN_NAV = [
  { label: 'Dashboard', to: '/admin', icon: '📊', end: true },
  { label: 'Approval Center', to: '/admin/approvals', icon: '✅' },
  { label: 'Stories', to: '/admin/stories', icon: '📰' },
  { label: 'News', to: '/admin/news', icon: '🗞️' },
  { label: 'Events', to: '/admin/events', icon: '📅' },
  { label: 'Activities', to: '/admin/activities', icon: '🤝' },
  { label: 'Members', to: '/admin/members', icon: '👥' },
  { label: 'Page Maker', to: '/admin/pages', icon: '🧩' },
  { label: 'Menu Management', to: '/admin/menus', icon: '🗂️' },
  { label: 'Media', to: '/admin/media', icon: '🖼️' },
  { label: 'Facebook Sync', to: '/admin/facebook', icon: '📘' },
];

export default function DashboardLayout({ variant = 'member' }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const nav = variant === 'admin' ? ADMIN_NAV : MEMBER_NAV;

  async function handleLogout() {
    await logout();
    toast.success('Signed out.');
    navigate('/');
  }

  return (
    <div className="flex min-h-screen bg-surface">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      {/* Sidebar */}
      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 w-64 transform bg-maroon-900 text-cream-100 transition-transform lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center border-b border-white/10 px-5">
          <Logo compact light />
          {/* `.eyebrow` defaults to gold-600, the light-background text gold;
              on the maroon-900 sidebar it needs the on-dark counterpart. */}
          <span className="ml-2 eyebrow text-gold-400">
            {variant === 'admin' ? 'Admin' : 'Member'}
          </span>
        </div>
        <nav className="space-y-1 p-3">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive ? 'bg-maroon-800 text-gold-400' : 'text-cream-100/80 hover:bg-maroon-800 hover:text-cream-100'
                )
              }
            >
              <span aria-hidden>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-white/10 p-3">
          <Link to="/" className="block w-full rounded-full px-4 py-2 text-center text-sm font-medium text-cream-100/80 transition-colors hover:bg-maroon-800 hover:text-cream-100">
            ← Back to site
          </Link>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-maroon-950/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-cream-300 bg-surface-card px-4 sm:px-6">
          <button className="lg:hidden text-maroon-700" onClick={() => setSidebarOpen(true)} aria-label="Open sidebar">
            ☰
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-ink">{user?.name}</p>
              <p className="text-xs capitalize text-ink-muted">{user?.role}</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-maroon-700 text-sm font-bold text-gold-400">
              {user?.name?.[0] || '?'}
            </div>
            <button onClick={handleLogout} className="btn-ghost text-sm">
              Logout
            </button>
          </div>
        </header>

        <main id="main" className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export { DashboardLayout };
